import DateFnsUtils from "@date-io/date-fns";
import {
  Backdrop,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { Cached } from "@material-ui/icons";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { format } from "date-fns";
import jaLocale from "date-fns/locale/ja";
import { format as formatTZ, toDate } from "date-fns-tz";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getReportCategory,
  languageType,
  reportData,
  reportFormLabel,
  targetData,
} from "../../../model/Report";
import {
  fetchListOfficeInfos,
  fetchUpdateSelectedOfficeInfo,
  selector as officeStoreSelector,
} from "../../../store/OfficeStore";
import {
  fetchCreatePDF,
  fetchCreateStart,
  fetchGetJudgedDates,
  fetchGetReportData,
  fetchResetReportData,
  fetchSendReportData,
  selector as reportStoreSelector,
} from "../../../store/ReportStore";
import { ProgressView, SelectOfficeField } from "../../share";
import useStyles from "../useStyles";

import ReportDateTable from "./ReportDateTable";
import ReportInputTable from "./ReportInputTable";

const iconColor = "inherit";
const iconSize = "large";
const iconEdge = "end";

class CustomUtils extends DateFnsUtils {
  getCalendarHeaderText(date) {
    return format(date, "yyyy/MM", { locale: this.locale });
  }

  getDatePickerHeaderText(date) {
    return format(date, "MM/dd(E)", { locale: this.locale });
  }

  getMonthText(date) {
    return format(date, "MM月", { locale: this.locale });
  }
}

/**
 * ReportCardコンポーネント.
 *
 * @return {object} コンポーネント
 */
function ReportCard() {
  // スタイル
  const styles = useStyles();

  // 表示言語
  const language = languageType.ja;

  // Dispatch
  const dispatch = useDispatch();

  // Store情報
  const {
    // 拠点情報一覧
    officeInfos,
    // 選択されている拠点情報
    selectedOfficeInfo,
  } = useSelector(officeStoreSelector);

  const {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading,
    // PDF生成中フラグ（true: 表示、false: 非表示）
    isCreating,
    // 判定結果入力済み日付
    judgedDates,
    // 診断カルテデータ(今回)
    currentData,
    // 診断カルテデータ(前回)
    beforeData1,
    // 診断カルテデータ(前々回)
    beforeData2,
  } = useSelector(reportStoreSelector);

  // 比較対象日付選択
  const [dateSelect, setDateSelect] = React.useState({
    currentDate: "",
    beforeDate1: "",
    beforeDate2: "",
  });

  // 診断カルテデータ(一時保存域)
  const [temporaryData, setTemporaryData] = React.useState({
    createdDay: formatTZ(toDate(new Date()), "yyyy-MM-dd", {
      timeZone: "Asia/Tokyo",
    }),
  });

  // 出力ファイル名
  const [fileName, setFileName] = React.useState(
    "拠点番号-拠点名_YYYY-mm-dd_DiagnosticReport"
  );

  // 診断カルテ書式
  const [categoryList, setCategoryList] = React.useState([]);

  // カルテデータ取得処理
  const getReportData = React.useCallback(
    (serialNumber, target, date) => {
      if (date) {
        dispatch(
          fetchGetReportData(
            serialNumber,
            target,
            date,
            selectedOfficeInfo.pressType
          )
        );
      } else {
        dispatch(fetchResetReportData(target));
      }
    },
    [dispatch, selectedOfficeInfo.pressType]
  );

  // 副作用(カード表示時、拠点一覧を再取得)
  React.useEffect(() => {
    dispatch(fetchListOfficeInfos());
  }, [dispatch]);

  // 副作用(拠点名選択時、カルテ作成済み日付一覧を取得)
  React.useEffect(() => {
    if (selectedOfficeInfo.serialNumber) {
      dispatch(fetchGetJudgedDates(selectedOfficeInfo.serialNumber));
      // 日付データを初期化
      setDateSelect({
        currentDate: "",
        beforeDate1: "",
        beforeDate2: "",
      });
      // 診断カルテデータを初期化
      dispatch(fetchResetReportData(targetData.current));
      dispatch(fetchResetReportData(targetData.before1));
      dispatch(fetchResetReportData(targetData.before2));
    }
  }, [dispatch, selectedOfficeInfo.serialNumber]);

  // 副作用(拠点名選択時、表示する診断カルテ書式を設定)
  React.useEffect(() => {
    setCategoryList(getReportCategory(selectedOfficeInfo.pressType) || []);
  }, [selectedOfficeInfo.pressType]);

  // 今回データ取得時、ステート変数にデータを展開する(画面入力情報との結合のため)
  React.useEffect(() => {
    const isObject = (obj) =>
      obj && typeof obj === "object" && !Array.isArray(obj);
    const resetData = (target, source) => {
      const result = { ...target };

      if (isObject(target) && isObject(source)) {
        const sourceEntry = Object.entries(source);

        sourceEntry.forEach((entry) => {
          const sourceKey = entry[0];
          const sourceValue = entry[1];
          const targetValue = target[sourceKey];

          if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
            // 対象Keyが配列の場合は結合
            result[sourceKey] = targetValue.concat(...sourceValue);
          } else if (
            isObject(sourceValue) &&
            Object.prototype.hasOwnProperty.call(target, sourceKey)
          ) {
            // 対象Keyがオブジェクトの場合は再帰的に処理
            result[sourceKey] = resetData(targetValue, sourceValue);
          } else {
            // 対象Keyが結合先に存在しない場合 or 単体Keyの場合は結合先に追加
            Object.assign(result, { [sourceKey]: sourceValue });
          }
        });
      }
      return result;
    };

    // APIから取得したデータを初期値として反映
    let data;
    if (isObject(currentData)) {
      data = resetData({}, currentData);
    } else {
      data = resetData({}, reportData);
    }

    data.createdDay =
      data.createdDay ||
      formatTZ(toDate(new Date()), "yyyy-MM-dd", { timeZone: "Asia/Tokyo" });
    data.companyName = data.companyName || selectedOfficeInfo.label;
    data.workNumber = data.workNumber || selectedOfficeInfo.serialNumber;

    setTemporaryData(data);
  }, [selectedOfficeInfo.serialNumber, selectedOfficeInfo.label, currentData]);

  // 拠点名、作成日変更時、出力ファイル名を更新
  React.useEffect(() => {
    let office = "拠点番号-拠点名";
    if (selectedOfficeInfo.serialNumber) {
      office = `${selectedOfficeInfo.serialNumber}-${selectedOfficeInfo.label}`;
    }
    setFileName(
      `${office}_${temporaryData.createdDay || "YYYY-mm-dd"}_DiagnosticReport`
    );
  }, [
    selectedOfficeInfo.serialNumber,
    selectedOfficeInfo.label,
    temporaryData.createdDay,
  ]);

  React.useEffect(() => {
    if (isCreating) {
      const payload = {
        fileName,
        pressType: selectedOfficeInfo.pressType,
        categoryList,
      };

      dispatch(fetchCreatePDF(payload));
    }
  }, [
    dispatch,
    isCreating,
    fileName,
    selectedOfficeInfo.pressType,
    categoryList,
  ]);

  // 拠点名選択時の処理
  const handleSelectChange = (event) => {
    if (event && event.target && event.target.value) {
      dispatch(fetchUpdateSelectedOfficeInfo(event.target.value));
    }
  };

  // 比較対象日付変更時の処理
  const handleDateChange = (event) => {
    const data = { ...dateSelect };
    // 入力アイテムに定義してある"name"から更新するプロパティを判定
    if (event && event.target && event.target.name) {
      const key = event.target.name;
      data[key] = event.target.value || "";
    }
    setDateSelect(data);
  };

  // 日付確定ボタン押下時の処理
  const handleClickConfirmDate = () => {
    if (selectedOfficeInfo.serialNumber) {
      getReportData(
        selectedOfficeInfo.serialNumber,
        targetData.current,
        dateSelect.currentDate
      );
      getReportData(
        selectedOfficeInfo.serialNumber,
        targetData.before1,
        dateSelect.beforeDate1
      );
      getReportData(
        selectedOfficeInfo.serialNumber,
        targetData.before2,
        dateSelect.beforeDate2
      );
    }
  };

  // カルテ情報更新処理
  const handleInformationChange = (event) => {
    // 入力アイテムに定義してある"name"から更新するプロパティを判定
    if (event && event.target && event.target.name) {
      const key = event.target.name;
      const data = { ...temporaryData };
      data[key] = event.target.value || "";

      // 【"使用年数"&"機番" 非表示対応】graphqlはそのまま、画面表示からは対象外とする
      // // 使用年数の場合、入力文字と桁数のガードを行う
      // if (key === "useYears" && event.target.value) {
      //   const value = [];
      //   let dotCount = 0;
      //   let decCount = 0;

      //   // 入力情報を1文字ずつ解析
      //   for (let i = 0; i < event.target.value.length; i++) {
      //     // Unicode変換
      //     const char = event.target.value.charCodeAt(i);

      //     if (char >= 0x30 && char <= 0x39) {
      //       // 半角数字(0-9)
      //       if (decCount < 1) {
      //         // 小数点1桁までは採用する
      //         value.push(event.target.value[i]);
      //       }
      //       if (dotCount > 0) {
      //         // ドットよりも後ろに入力されている場合は小数桁としてカウントする
      //         decCount += 1;
      //       }
      //     } else if (char === 0x2e) {
      //       // ドット( . )
      //       if (i > 0 && dotCount < 1) {
      //         // 先頭文字ではない、かつ最初のドットだった場合は採用する
      //         value.push(event.target.value[i]);
      //       }
      //       dotCount += 1;
      //     }
      //   }
      //   data[key] = value.join("");
      // }
      setTemporaryData(data);
    }
  };

  // 納入日更新処理(Date Picker)
  const handleDueDateChange = (newDate) => {
    const data = { ...temporaryData };
    let value = "";

    if (newDate) {
      value = formatTZ(toDate(newDate), "yyyy-MM", { timeZone: "Asia/Tokyo" });
    }
    data.dueDate = value;
    setTemporaryData(data);
  };

  // カルテ内容更新処理
  const handleInputChange = (event) => {
    const isObject = (obj) =>
      obj && typeof obj === "object" && !Array.isArray(obj);
    const resetData = (target, source) => {
      const result = { ...target };

      if (isObject(target) && isObject(source)) {
        const sourceEntry = Object.entries(source);

        sourceEntry.forEach((entry) => {
          const sourceKey = entry[0];
          const sourceValue = entry[1];
          const targetValue = target[sourceKey];

          if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
            // 対象Keyが配列の場合は結合
            result[sourceKey] = targetValue.concat(...sourceValue);
          } else if (
            isObject(sourceValue) &&
            Object.prototype.hasOwnProperty.call(target, sourceKey)
          ) {
            // 対象Keyがオブジェクトの場合は再帰的に処理
            result[sourceKey] = resetData(targetValue, sourceValue);
          } else {
            // 対象Keyが結合先に存在しない場合 or 単体Keyの場合は結合先に追加
            Object.assign(result, { [sourceKey]: sourceValue });
          }
        });
      }
      return result;
    };

    let obj = {};

    if (event && event.target && event.target.name) {
      const keys = event.target.name.split(".");
      const category = String(keys[0]);
      const item = String(keys[1]);

      if (item === "note") {
        // 更新箇所が[備考]の場合
        obj = { [category]: { note: event.target.value || "" } };
      } else {
        // 更新箇所が[判定]の場合
        obj = { [category]: { [item]: { judge: event.target.value || "" } } };
      }

      const data = resetData(temporaryData, obj);
      setTemporaryData(data);
    }
  };

  // 出力ファイル名変更時の処理
  const handleFileNameChange = (event) => {
    if (event && event.target && event.target.value) {
      setFileName(event.target.value);
    }
  };

  // カルテ登録ボタン押下時の処理
  const handleClickUpdateReport = () => {
    const isObject = (obj) =>
      obj && typeof obj === "object" && !Array.isArray(obj);
    const resetData = (target, source) => {
      const result = {};

      if (isObject(target) && isObject(source)) {
        const targetEntry = Object.entries(target);

        targetEntry.forEach((entry) => {
          const targetKey = entry[0];
          const targetValue = entry[1];
          const sourceValue = source[targetKey];

          if (
            targetKey !== "__typename" &&
            targetKey !== "createdAt" &&
            targetKey !== "updatedAt" &&
            targetKey !== "difference" &&
            targetKey !== "per"
          ) {
            if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
              result[targetKey] = targetValue.concat(...sourceValue);
            } else if (isObject(targetValue)) {
              result[targetKey] = resetData(targetValue, {});
            } else if (
              Object.prototype.hasOwnProperty.call(source, targetKey)
            ) {
              Object.assign(result, { [targetKey]: sourceValue });
            } else {
              Object.assign(result, { [targetKey]: targetValue });
            }
          }
        });
      }

      return result;
    };

    const obj = {};
    // 【"使用年数"&"機番" 非表示対応】graphqlはそのまま、画面表示からは対象外とする
    // obj.useYears = parseFloat(temporaryData.useYears);
    obj.creationResult = true;
    obj.pressType = selectedOfficeInfo.pressType;

    const payload = resetData(temporaryData, obj);
    dispatch(fetchSendReportData(payload));
  };

  return (
    <>
      <Card className={styles.card}>
        <CardContent className={`${styles.cardContent} ${styles.flexStart}`}>
          <SelectOfficeField
            label="拠点名"
            value={selectedOfficeInfo.serialNumber}
            onChange={handleSelectChange}
            items={officeInfos}
          />
          <div className={styles.marginLeftAuto}>
            <IconButton
              color={iconColor}
              edge={iconEdge}
              disabled={!selectedOfficeInfo.serialNumber}
              onClick={() => dispatch(fetchListOfficeInfos())}
            >
              <Cached fontSize={iconSize} />
            </IconButton>
          </div>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Grid className={styles.reportGrid} container spacing={1}>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="currentDate"
                type="date"
                label="今回の日付"
                InputLabelProps={{
                  shrink: true,
                }}
                value={dateSelect.currentDate}
                variant="standard"
                onChange={handleDateChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="beforeDate1"
                type="date"
                label="前回の日付"
                InputLabelProps={{
                  shrink: true,
                }}
                value={dateSelect.beforeDate1}
                variant="standard"
                onChange={handleDateChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="beforeDate2"
                type="date"
                label="前々回の日付"
                InputLabelProps={{
                  shrink: true,
                }}
                value={dateSelect.beforeDate2}
                variant="standard"
                onChange={handleDateChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <Button
                className={styles.reportInputButton}
                variant="contained"
                color="primary"
                onClick={() => handleClickConfirmDate()}
                disabled={
                  !selectedOfficeInfo.serialNumber || !dateSelect.currentDate
                }
              >
                日付確定
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <ReportDateTable dateList={judgedDates || []}/>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Grid className={styles.reportGrid} container spacing={1}>
            <Grid className={styles.reportGridItemLarge} item>
              <TextField
                className={styles.reportInputTextField}
                type="text"
                label="ファイル名"
                InputLabelProps={{
                  shrink: true,
                }}
                value={fileName}
                variant="standard"
                onChange={handleFileNameChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <Button
                className={styles.reportInputButton}
                variant="contained"
                color="primary"
                onClick={() => dispatch(fetchCreateStart(languageType.ja))}
                disabled={
                  !fileName ||
                  !temporaryData.companyName ||
                  !temporaryData.workNumber ||
                  !temporaryData.creationResult
                }
              >
                JPNファイル出力
              </Button>
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <Button
                className={styles.reportInputButton}
                variant="contained"
                color="primary"
                onClick={() => dispatch(fetchCreateStart(languageType.en))}
                disabled={
                  !fileName ||
                  !temporaryData.companyName ||
                  !temporaryData.workNumber ||
                  !temporaryData.creationResult
                }
              >
                ENGファイル出力
              </Button>
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <Button
                className={styles.reportInputButton}
                variant="contained"
                color="primary"
                onClick={() => handleClickUpdateReport()}
                disabled={
                  !selectedOfficeInfo.serialNumber ||
                  !dateSelect.currentDate ||
                  !currentData.office
                }
              >
                カルテ登録
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Grid className={styles.reportGrid} container spacing={1}>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="createdDay"
                type="date"
                label={reportFormLabel.createdDay[language]}
                InputLabelProps={{
                  shrink: true,
                }}
                value={temporaryData.createdDay}
                variant="outlined"
                onChange={handleInformationChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="companyName"
                type="text"
                label={reportFormLabel.companyName[language]}
                InputLabelProps={{
                  shrink: true,
                }}
                value={temporaryData.companyName || ""}
                variant="outlined"
                onChange={handleInformationChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="workNumber"
                type="text"
                label={reportFormLabel.workNumber[language]}
                InputLabelProps={{
                  shrink: true,
                }}
                value={temporaryData.workNumber || ""}
                variant="outlined"
                onChange={handleInformationChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="model"
                type="text"
                label={reportFormLabel.model[language]}
                InputLabelProps={{
                  shrink: true,
                }}
                value={temporaryData.model || ""}
                variant="outlined"
                onChange={handleInformationChange}
              />
            </Grid>
            {/* 【"使用年数"&"機番" 非表示対応】graphqlはそのまま、画面表示からは対象外とする */}
            {/* <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="useYears"
                type="text"
                label={reportFormLabel.useYears[language]}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">年</InputAdornment>
                  ),
                }}
                value={temporaryData.useYears}
                variant="outlined"
                onChange={handleInformationChange}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <TextField
                className={styles.reportInputTextField}
                name="modelNumber"
                type="text"
                label={reportFormLabel.modelNumber[language]}
                InputLabelProps={{
                  shrink: true,
                }}
                value={temporaryData.modelNumber}
                variant="outlined"
                onChange={handleInformationChange}
              />
            </Grid> */}
            <Grid className={styles.reportGridItemSmall} item>
              <MuiPickersUtilsProvider utils={CustomUtils} locale={jaLocale}>
                <DatePicker
                  clearable
                  name="dueDate"
                  format="yyyy/MM"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputVariant="outlined"
                  label={reportFormLabel.dueDate[language]}
                  onChange={handleDueDateChange}
                  openTo="year"
                  value={
                    temporaryData.dueDate
                      ? new Date(temporaryData.dueDate)
                      : null
                  }
                  variant="dialog"
                  views={["year", "month"]}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
        </CardContent>
        {selectedOfficeInfo.pressType ? (
          <CardContent className={styles.cardContent}>
            <Typography variant="h6">
              プレス機種：{selectedOfficeInfo.pressType}
            </Typography>
            <ReportInputTable
              categoryList={categoryList}
              temporaryData={temporaryData}
              beforeData1={beforeData1}
              beforeData2={beforeData2}
              onChange={handleInputChange}
              onChangeOther={handleInformationChange}
            />
          </CardContent>
        ) : (
          <></>
        )}
      </Card>
      <Backdrop className={styles.backdrop} open={isLoading}>
        <ProgressView />
      </Backdrop>
    </>
  );
}

export default ReportCard;
